import { renderToStaticMarkup } from 'react-dom/server';
import styled from 'styled-components';
import { gridConfig } from '../../fuse-responsive-grid/src/grid';

const linkConfig = {
	large: {
		fontSize: `var(--font-size-1)`,
		lineHeight: `var(--font-line-height-1)`,
		iconMarginSide: `var(--spacing-global-space2)`,
		iconSize: '20px',
		underlineOffset: '7px',
	},
	medium: {
		fontSize: `var(--font-size-m1)`,
		lineHeight: `var(--font-line-height-m1)`,
		iconMarginSide: `var(--spacing-global-space1)`,
		iconSize: '18px',
		underlineOffset: '5px',
	},
	small: {
		fontSize: `var(--font-size-m2)`,
		lineHeight: `var(--font-line-height-m2)`,
		iconMarginSide: `var(--spacing-global-space1)`,
		iconSize: '16px',
		underlineOffset: '4px',
	},
};
// Generate CSS link text and padding based on size and icon
const linkSizeCss = (size, iconRight, icon) => `
	line-height: calc(${linkConfig[size].lineHeight} + 2px);
	text-underline-offset: ${linkConfig[size].underlineOffset};
	font-size: ${linkConfig[size].fontSize};

	${
	!iconRight && icon
		? `padding:0 0 0 calc(${linkConfig[size].lineHeight} + ${linkConfig[size].iconMarginSide});`
		: ``
}
`;
// Generate CSS link icon based on size and icon
const iconSizeCss = (size, iconRight, icon) => {
	const Icon = icon;

	return `
	${
	iconRight
		? `	margin:0 0 0 ${linkConfig[size].iconMarginSide};
				height: ${linkConfig[size].lineHeight};`
		: ` position: absolute;
				top:0;
				left:0;
				height: calc(${linkConfig[size].lineHeight} + 2px);`
}

	width: calc(${linkConfig[size].lineHeight} - 1px);
	mask: url("data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(Icon))}") center/${
	linkConfig[size].lineHeight
} no-repeat;
	-webkit-mask: url("data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(Icon))}") center/${
	linkConfig[size].lineHeight
} no-repeat;
`;
};

const StyledLink = styled.a
	.withConfig({
		shouldForwardProp: (prop) =>
			![
				'centreAlign',
				'focusable',
				'icon',
				'iconRight',
				'imagePosition',
				'isButton',
				'size',
				'standalone',
				'underline',
				'white',
			].includes(prop),
	})
	.attrs({
		className: 'fuse-styled-link',
	})`
	${({ centreAlign, focusable, icon, iconRight, isButton, size, standalone, underline, white }) => {
		// Array of breakpoints i.e. xxs, xs, s, m, l, xl, xxl
		const breakpointsList = Object.keys(gridConfig);

		// Array of valid viewports from size prop passed
		const viewports =
			typeof size === 'object' &&
			Object.keys(size).filter((item) => breakpointsList.includes(item));
		// Check if size prop passed is responsive based on viewports above
		const isResponsive = viewports.length > 0;
		// Function to set CSS based on array of viewports
		const setViewportCss = (cssForViewport) =>
			viewports.reduce((acc, viewport) => {
				const viewportSize = size[viewport];
				const viewportCss = `
					@media screen and (min-width: ${gridConfig[viewport].minWidth}) {
						${cssForViewport(viewportSize, iconRight, icon)}
					}
				`;
				return acc + viewportCss;
			}, '');
		const iconPseudoEl = iconRight ? 'after' : 'before';
		const textDecorationUnderline = (colour) => `
			text-decoration-line: underline;
			text-decoration-color: ${white ? `var(--color-global-white)` : colour};
			text-decoration-style: solid;
			text-decoration-thickness: 1px;
		`;
		// Set colour when white prop or variant is false
		const setColour = (colour) => (white ? `var(--color-global-white)` : colour);

		return `
			color: ${setColour('var(--color-global-grey900)')};
			display: ${standalone ? 'block' : 'inline-block'};
			font-weight: var(--font-weight-paragraph);
			${
	standalone
		? `
						width: fit-content;
						margin: var(--spacing-global-space5) ${centreAlign ? 'auto' : '0'}
								var(--spacing-global-space7);
						@media screen and (min-width: ${gridConfig.m.minWidth}) {
							margin: var(--spacing-global-space6) ${centreAlign ? 'auto' : '0'}
								var(--spacing-global-space9);
						}
					`
		: ``
}
			${
	underline
		? textDecorationUnderline(`var(--color-global-grey500)`)
		: `text-decoration-line: none;`
}
			${
	isButton
		? `
						border: unset;
						background: unset;
						cursor: pointer;
						padding: 0;
					`
		: ``
}

			${isResponsive ? setViewportCss(linkSizeCss) : linkSizeCss(size, iconRight, icon)}

			&:hover,
			&:focus {
				color: ${setColour('var(--color-brand-dark)')};
				${textDecorationUnderline(`var(--color-brand-dark)`)}
			}

			&:active {
				color: ${setColour('var(--color-global-black)')};
				${textDecorationUnderline(`var(--color-global-black)`)}
			}

			${
	focusable
		? `
					&:active,
					&:focus {
						outline: var(--border-radius-s, 2px) solid var(--color-functional-focus);
						outline-offset: 0;
					}`
		: ''
}

			&[aria-disabled] {
				color: ${setColour('var(--color-global-grey400)')};
				text-decoration-line: none;
				${underline ? textDecorationUnderline(`var(--color-global-grey400)`) : ``}
				outline: none;
			}

			/* invisible pseudo element to make the tappable area on buttons at least 44x44px */
			position: relative;

			${iconRight ? `&:before` : `&:after`} {
				content: '';
				display: block;
				min-height: 44px;
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				min-width: 44px;
				width: 100%;
			}

			${
	icon
		? `
					&:hover:${iconPseudoEl},
					&:focus:${iconPseudoEl} {
						background: ${setColour('var(--color-brand-dark)')};
					}

					&:active:${iconPseudoEl} {
						background: ${setColour('var(--color-global-black)')};
					}

					&[aria-disabled]:${iconPseudoEl} {
						background: ${setColour('var(--color-global-grey400)')};
					}

					&:${iconPseudoEl} {
						content: '';
						display: inline-block;
						background: ${setColour('var(--color-brand-mid)')};
						vertical-align: text-bottom;

						${isResponsive ? setViewportCss(iconSizeCss) : iconSizeCss(size, iconRight, icon)}
					}
				`
		: ``
}
		`;
	}}
`;

export default StyledLink;
